import React, { useEffect, useState } from 'react';
import { Box, Chip, Grid, Slider, TextField, Typography } from '@mui/material';
import type { PortalRecipePayload } from '../../portal/portalDraftEnvelope';
import {
  RECIPE_COURSES_ROW1,
  RECIPE_COURSES_ROW2,
  RECIPE_DIFFICULTIES,
  RECIPE_PREP_LABELS,
  RECIPE_SERVINGS,
  difficultySliderIndex,
  normalizeCourseLabel,
  normalizeDifficultyLabel,
  prepLabelFromMinutes,
  prepMinutesFromLabel,
  prepSliderIndex,
  servingsLabelForValue,
  servingsSliderIndex,
  type RecipePrepLabel,
} from '../../utils/recipeDraftHelpers';
import { DraftHeroBanner } from './DraftHeroBanner';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fafafa',
    '& fieldset': { borderColor: '#3f3f46' },
    '&:hover fieldset': { borderColor: '#a1a1aa' },
    '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
  },
  '& .MuiInputLabel-root': { color: '#a1a1aa' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' },
};

const chipOutline = {
  borderColor: '#3f3f46',
  color: '#fafafa',
  bgcolor: 'transparent',
  '&:hover': { bgcolor: '#27272a' },
};

const chipSelected = {
  borderColor: '#60a5fa',
  color: '#0a0a0a',
  bgcolor: '#60a5fa',
  '&:hover': { bgcolor: '#3b82f6' },
};

const sliderSx = {
  mt: 0.5,
  mb: 0.5,
  width: '100%',
  maxWidth: '100%',
  color: '#60a5fa',
  '& .MuiSlider-thumb': { bgcolor: '#60a5fa' },
  '& .MuiSlider-track': { bgcolor: '#60a5fa' },
  '& .MuiSlider-rail': { bgcolor: '#3f3f46' },
  '& .MuiSlider-mark': { bgcolor: '#52525b' },
  '& .MuiSlider-markLabel': { color: '#a1a1aa', fontSize: '0.72rem', whiteSpace: 'nowrap' },
};

export type RecipeDraftPanelProps = {
  recipe: PortalRecipePayload;
  saveRecipe: (next: PortalRecipePayload) => void;
  sourceClustersSlot?: React.ReactNode;
  draftAccessBar?: React.ReactNode;
  /** Title + access rendered in Library drawer header. */
  accessInHeader?: boolean;
  /** When true, all fields are disabled (ready to publish). */
  readOnly?: boolean;
};

export function RecipeDraftPanel({
  recipe,
  saveRecipe,
  sourceClustersSlot,
  draftAccessBar,
  accessInHeader = false,
  readOnly = false,
}: RecipeDraftPanelProps) {
  const [courseUi, setCourseUi] = useState(() => normalizeCourseLabel(recipe.course));
  const [difficultyUi, setDifficultyUi] = useState(() => normalizeDifficultyLabel(recipe.difficulty));
  const [prepLabelUi, setPrepLabelUi] = useState(() => prepLabelFromMinutes(recipe.prepMinutes));
  const [servingsUi, setServingsUi] = useState(() => servingsLabelForValue(recipe.servings));

  useEffect(() => {
    setCourseUi(normalizeCourseLabel(recipe.course));
    setDifficultyUi(normalizeDifficultyLabel(recipe.difficulty));
    setPrepLabelUi(prepLabelFromMinutes(recipe.prepMinutes));
    setServingsUi(servingsLabelForValue(recipe.servings));
  }, [recipe.id, recipe.course, recipe.difficulty, recipe.prepMinutes, recipe.servings]);

  const ingredientsText = recipe.ingredients.join('\n');
  const stepsText = recipe.steps.join('\n');

  const syncCourse = (label: string) => {
    setCourseUi(label);
    saveRecipe({ ...recipe, course: label });
  };

  const syncDifficulty = (label: string) => {
    setDifficultyUi(label);
    saveRecipe({ ...recipe, difficulty: label });
  };

  const syncPrep = (label: RecipePrepLabel) => {
    setPrepLabelUi(label);
    saveRecipe({ ...recipe, prepMinutes: prepMinutesFromLabel(label) });
  };

  const syncServings = (label: string, value: number) => {
    setServingsUi(label);
    saveRecipe({ ...recipe, servings: value });
  };

  const prepMarks = RECIPE_PREP_LABELS.map((p, i) => ({
    value: i,
    label: p === '120+' ? '120+' : `${p} min`,
  }));

  const servingsMarks = RECIPE_SERVINGS.map((r, i) => ({ value: i, label: r.label }));

  return (
    <Box sx={{ minHeight: 500 }}>
      {!accessInHeader ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ color: '#fafafa' }}>
            Recipe draft
          </Typography>
          {draftAccessBar ? (
            <Box sx={{ flex: 1, minWidth: 200, maxWidth: 520, textAlign: 'right' }}>{draftAccessBar}</Box>
          ) : null}
        </Box>
      ) : null}

      <DraftHeroBanner
        imageUrl={recipe.image}
        onImageUrlCommitted={(url) => saveRecipe({ ...recipe, image: url })}
        ariaLabel="Recipe hero image"
        maxHeight={200}
        required
        readOnly={readOnly}
      />

      <Box component="fieldset" disabled={readOnly} sx={{ border: 'none', m: 0, p: 0, minWidth: 0 }}>
        {sourceClustersSlot ? <Box sx={{ mb: 2 }}>{sourceClustersSlot}</Box> : null}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {!accessInHeader ? (
              <TextField
                fullWidth
                required
                label="Title"
                value={recipe.title}
                onChange={(e) => saveRecipe({ ...recipe, title: e.target.value })}
                sx={{ ...fieldSx, mb: 2 }}
              />
            ) : null}
            <TextField
              fullWidth
              label="Description"
              value={recipe.description}
              onChange={(e) => saveRecipe({ ...recipe, description: e.target.value })}
              sx={{ ...fieldSx, mb: 2 }}
            />

            <Grid container spacing={2} sx={{ mb: 1.5, alignItems: 'stretch' }}>
              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    height: '100%',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #2a2f2a',
                    bgcolor: 'rgba(52, 211, 153, 0.06)',
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#86b89a', display: 'block', mb: 1, letterSpacing: 0.02 }}>
                    Course
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: '#a1a1aa', mb: 0.75 }}>
                    Select one <Typography component="span" sx={{ color: '#f87171' }}>*</Typography>
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1,
                      mb: 1,
                      width: '100%',
                      gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(7, minmax(0, 1fr))' },
                    }}
                  >
                    {RECIPE_COURSES_ROW1.map((c) => (
                      <Chip
                        key={c}
                        label={c}
                        onClick={() => syncCourse(c)}
                        variant={courseUi === c ? 'filled' : 'outlined'}
                        sx={{
                          width: '100%',
                          justifyContent: 'center',
                          ...(courseUi === c ? { ...chipSelected } : { ...chipOutline }),
                        }}
                      />
                    ))}
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1,
                      width: '100%',
                      gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(6, minmax(0, 1fr))' },
                    }}
                  >
                    {RECIPE_COURSES_ROW2.map((c) => (
                      <Chip
                        key={c}
                        label={c}
                        onClick={() => syncCourse(c)}
                        variant={courseUi === c ? 'filled' : 'outlined'}
                        sx={{
                          width: '100%',
                          justifyContent: 'center',
                          ...(courseUi === c ? { ...chipSelected } : { ...chipOutline }),
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    height: '100%',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #27272a',
                    bgcolor: 'rgba(59, 130, 250, 0.07)',
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1.25, letterSpacing: 0.02 }}>
                    Time & difficulty
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: '#e4e4e7', mb: 0.25, fontWeight: 700 }}
                  >
                    Difficulty <Typography component="span" sx={{ color: '#f87171', fontWeight: 700 }}>*</Typography>
                  </Typography>
                  <Slider
                    sx={sliderSx}
                    value={difficultySliderIndex(difficultyUi)}
                    onChange={(_, v) => {
                      const i = v as number;
                      syncDifficulty(RECIPE_DIFFICULTIES[Math.min(2, Math.max(0, i))]);
                    }}
                    step={null}
                    min={0}
                    max={2}
                    marks={RECIPE_DIFFICULTIES.map((d, i) => ({ value: i, label: d }))}
                    valueLabelDisplay="off"
                  />

                  <Typography
                    variant="subtitle2"
                    sx={{ color: '#e4e4e7', mb: 0.25, mt: 1, fontWeight: 700 }}
                  >
                    Prep time (minutes){' '}
                    <Typography component="span" sx={{ color: '#f87171', fontWeight: 700 }}>*</Typography>
                  </Typography>
                  <Slider
                    sx={sliderSx}
                    value={prepSliderIndex(prepLabelUi)}
                    onChange={(_, v) => {
                      const i = v as number;
                      const label = RECIPE_PREP_LABELS[Math.min(RECIPE_PREP_LABELS.length - 1, Math.max(0, i))];
                      syncPrep(label);
                    }}
                    step={null}
                    min={0}
                    max={RECIPE_PREP_LABELS.length - 1}
                    marks={prepMarks}
                    valueLabelDisplay="off"
                  />
                </Box>
              </Grid>
            </Grid>

            <Box
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 1,
                border: '1px solid #27272a',
                bgcolor: 'rgba(96, 165, 250, 0.05)',
              }}
            >
              <Typography variant="subtitle2" sx={{ color: '#e4e4e7', mb: 0.25, fontWeight: 700 }}>
                Servings <Typography component="span" sx={{ color: '#f87171', fontWeight: 700 }}>*</Typography>
              </Typography>
              <Slider
                sx={sliderSx}
                value={servingsSliderIndex(servingsUi)}
                onChange={(_, v) => {
                  const i = v as number;
                  const row = RECIPE_SERVINGS[Math.min(RECIPE_SERVINGS.length - 1, Math.max(0, i))];
                  syncServings(row.label, row.value);
                }}
                step={null}
                min={0}
                max={RECIPE_SERVINGS.length - 1}
                marks={servingsMarks}
                valueLabelDisplay="off"
              />
            </Box>

            <TextField
              fullWidth
              multiline
              required
              minRows={4}
              label="Ingredients (one per line)"
              value={ingredientsText}
              onChange={(e) =>
                saveRecipe({
                  ...recipe,
                  ingredients: e.target.value
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              sx={{ ...fieldSx, mb: 2, mt: 2 }}
            />
            <TextField
              fullWidth
              multiline
              required
              minRows={6}
              label="Steps (one per line)"
              value={stepsText}
              onChange={(e) =>
                saveRecipe({
                  ...recipe,
                  steps: e.target.value
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              sx={{ ...fieldSx, mb: 2 }}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
